import { useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { Project } from '@hangar/shared';
import { useCan, type Can } from '@/lib/permissions';
import type { RowAction } from '@/components/RowActions';
import { ArchiveProjectDialog } from './ArchiveProjectDialog';
import { EditProjectDialog } from './EditProjectDialog';

export interface ProjectActions {
  /** `POST /projects` needs `admin`. */
  canCreate: Can;
  /** Settings · Edit · Archive for one project (`projects:write` on it for the last two). */
  actionsFor: (project: Project) => RowAction[];
  /** Edit dialog and archive confirmation; render once. */
  overlays: ReactNode;
}

/** Edit and archive projects (the create form is NewProjectForm, inline where it is used). */
export function useProjectActions({ onArchived }: { onArchived?: (project: Project) => void } = {}): ProjectActions {
  const canCreate = useCan('admin');
  const canWrite = useCan('projects:write');
  const navigate = useNavigate();
  const [editing, setEditing] = useState<Project | null>(null);
  const [archiving, setArchiving] = useState<Project | null>(null);
  const reason = canWrite.allowed ? undefined : canWrite.reason;

  const actionsFor = (project: Project): RowAction[] =>
    canWrite.hidden
      ? []
      : [
          { key: 'settings', label: 'Settings', icon: 'settings', testId: 'project-settings', onSelect: () => void navigate({ to: '/projects/$slug/settings', params: { slug: project.slug } }) },
          { key: 'edit', label: 'Edit', icon: 'edit', testId: 'project-edit', disabledReason: reason, onSelect: () => setEditing(project) },
          { key: 'archive', label: 'Archive', icon: 'archive', testId: 'project-archive', destructive: true, disabledReason: reason, onSelect: () => setArchiving(project) },
        ];

  const overlays = (
    <>
      <EditProjectDialog project={editing} onOpenChange={(o) => (o ? undefined : setEditing(null))} />
      <ArchiveProjectDialog project={archiving} onOpenChange={(o) => (o ? undefined : setArchiving(null))} onArchived={onArchived} />
    </>
  );

  return { canCreate, actionsFor, overlays };
}
